/**
 * Lightweight imperative toast. No deps. Single global store.
 *
 * Usage:
 *   toast.success('Saved!');
 *   toast.error('Could not save');
 *   <Toaster /> mounted once near the root
 */
import { useEffect, useState } from 'react';

type Variant = 'success' | 'error' | 'info';
interface ToastItem { id: number; msg: string; variant: Variant; }

let nextId = 1;
const listeners = new Set<(items: ToastItem[]) => void>();
let items: ToastItem[] = [];

function emit() { for (const l of listeners) l(items); }
function push(msg: string, variant: Variant) {
  const item = { id: nextId++, msg, variant };
  items = [...items, item];
  emit();
  setTimeout(() => {
    items = items.filter((i) => i.id !== item.id);
    emit();
  }, 3500);
}

export const toast = {
  success: (m: string) => push(m, 'success'),
  error:   (m: string) => push(m, 'error'),
  info:    (m: string) => push(m, 'info'),
};

export function Toaster() {
  const [list, setList] = useState<ToastItem[]>(items);
  useEffect(() => {
    listeners.add(setList);
    return () => { listeners.delete(setList); };
  }, []);

  return (
    <div className="fixed top-3 right-3 z-[60] space-y-2 pointer-events-none">
      {list.map((t) => (
        <div
          key={t.id}
          className={`px-4 py-2 rounded-lg shadow text-sm text-white pointer-events-auto ${
            t.variant === 'success' ? 'bg-emerald-600'
            : t.variant === 'error' ? 'bg-rose-600'
            : 'bg-slate-800'
          }`}
        >
          {t.msg}
        </div>
      ))}
    </div>
  );
}
